import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/features/auth'
import type {
  PatientRecordWithRelations,
  Tag,
} from '@repo/shared'

// ============================================================
// Types
// ============================================================

interface FetchRecordsResult {
  data: PatientRecordWithRelations[]
  count: number
}

interface CreateRecordInput {
  patient_id: string
  content: string
  visibility: 'private' | 'shared' | 'family'
  tag_ids?: string[]
  record_date?: string // ISO 8601 datetime
}

interface UpdateRecordInput {
  id: string
  patient_id: string
  content?: string
  visibility?: 'private' | 'shared' | 'family'
  tag_ids?: string[]
  record_date?: string // ISO 8601 datetime
}

interface DeleteRecordInput {
  id: string
  patient_id: string
}

// ============================================================
// Fetch functions
// ============================================================

async function fetchPatientRecords(
  patientId: string,
  page: number = 1,
  perPage: number = 20,
  search?: string,
): Promise<FetchRecordsResult> {
  const offset = (page - 1) * perPage

  let query = supabase
    .from('patient_records')
    .select(
      '*, record_attachments(*), record_tags(tag_id, tags(*)), author:profiles!author_id(id, full_name, email)',
      { count: 'exact' },
    )
    .eq('patient_id', patientId)
    .eq('status', 'active')

  if (search && search.trim()) {
    query = query.ilike('content', `%${search.trim()}%`)
  }

  const { data, error, count } = await query
    .order('record_date', { ascending: false })
    .range(offset, offset + perPage - 1)

  if (error) throw error

  // Transform the nested record_tags into a flat tags array
  const records = (data ?? []).map((record) => {
    const tags: Tag[] = (record.record_tags ?? [])
      .map((rt: { tags: Tag | null }) => rt.tags)
      .filter(Boolean) as Tag[]

    return {
      ...record,
      tags,
      attachments: record.record_attachments ?? [],
      record_tags: undefined,
      record_attachments: undefined,
    } as PatientRecordWithRelations
  })

  return { data: records, count: count ?? 0 }
}

// ============================================================
// Hooks
// ============================================================

export function usePatientRecords(
  patientId: string,
  page: number = 1,
  perPage: number = 20,
  search?: string,
) {
  return useQuery({
    queryKey: ['patient-records', patientId, page, perPage, search],
    queryFn: () => fetchPatientRecords(patientId, page, perPage, search),
    enabled: !!patientId,
  })
}

export function useCreateRecord() {
  const queryClient = useQueryClient()
  const { userProfile, currentOrganization } = useAuth()

  return useMutation({
    mutationFn: async (input: CreateRecordInput) => {
      if (!userProfile) throw new Error('Usuario nao autenticado')
      if (!currentOrganization) throw new Error('Usuario sem organizacao ativa')
      if (currentOrganization.userRole === 'assistant') {
        throw new Error('Assistentes nao podem criar registros clinicos')
      }

      // 1. Insert the record
      const insertPayload: Record<string, unknown> = {
        patient_id: input.patient_id,
        author_id: userProfile.id,
        organization_id: currentOrganization.id,
        content: input.content,
        visibility: input.visibility,
      }
      if (input.record_date) {
        insertPayload.record_date = input.record_date
      }

      const { data: record, error: recordError } = await supabase
        .from('patient_records')
        .insert(insertPayload)
        .select()
        .single()

      if (recordError) throw recordError

      // 2. Insert tag associations if provided
      if (input.tag_ids && input.tag_ids.length > 0) {
        const tagInserts = input.tag_ids.map((tag_id) => ({
          record_id: record.id,
          tag_id,
        }))

        const { error: tagError } = await supabase
          .from('record_tags')
          .insert(tagInserts)

        if (tagError) throw tagError
      }

      // 3. Insert audit log
      const { error: auditError } = await supabase
        .from('record_audit_logs')
        .insert({
          record_id: record.id,
          action: 'created' as const,
          actor_id: userProfile.id,
        })

      if (auditError) {
        console.error('Failed to create audit log:', auditError)
        // Don't throw - audit failure shouldn't block record creation
      }

      return record
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['patient-records', variables.patient_id],
      })
    },
  })
}

export function useUpdateRecord() {
  const queryClient = useQueryClient()
  const { userProfile, currentOrganization } = useAuth()

  return useMutation({
    mutationFn: async (input: UpdateRecordInput) => {
      if (!userProfile) throw new Error('Usuario nao autenticado')
      if (!currentOrganization) throw new Error('Usuario sem organizacao ativa')
      if (currentOrganization.userRole === 'assistant') {
        throw new Error('Assistentes nao podem alterar registros clinicos')
      }

      // 1. Update the record
      const updateData: Record<string, unknown> = {}
      if (input.content !== undefined) updateData.content = input.content
      if (input.visibility !== undefined) updateData.visibility = input.visibility
      if (input.record_date !== undefined) updateData.record_date = input.record_date

      const { data: record, error: recordError } = await supabase
        .from('patient_records')
        .update(updateData)
        .eq('id', input.id)
        .select()
        .single()

      if (recordError) throw recordError

      // 2. Sync tags if provided
      if (input.tag_ids !== undefined) {
        // Delete existing tags
        const { error: deleteError } = await supabase
          .from('record_tags')
          .delete()
          .eq('record_id', input.id)

        if (deleteError) throw deleteError

        // Insert new tags
        if (input.tag_ids.length > 0) {
          const tagInserts = input.tag_ids.map((tag_id) => ({
            record_id: input.id,
            tag_id,
          }))

          const { error: tagError } = await supabase
            .from('record_tags')
            .insert(tagInserts)

          if (tagError) throw tagError
        }
      }

      // 3. Insert audit log
      const action = input.visibility !== undefined && input.content === undefined
        ? 'visibility_changed' as const
        : 'edited' as const

      const metadata: Record<string, unknown> = {}
      if (input.visibility !== undefined) {
        metadata.visibility = input.visibility
      }

      const { error: auditError } = await supabase
        .from('record_audit_logs')
        .insert({
          record_id: input.id,
          action,
          actor_id: userProfile.id,
          metadata: Object.keys(metadata).length > 0 ? metadata : null,
        })

      if (auditError) {
        console.error('Failed to create audit log:', auditError)
      }

      return record
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['patient-records', variables.patient_id],
      })
    },
  })
}

export function useDeleteRecord() {
  const queryClient = useQueryClient()
  const { userProfile, currentOrganization } = useAuth()

  return useMutation({
    mutationFn: async (input: DeleteRecordInput) => {
      if (!userProfile) throw new Error('Usuario nao autenticado')
      if (!currentOrganization) throw new Error('Usuario sem organizacao ativa')
      if (currentOrganization.userRole === 'assistant') {
        throw new Error('Assistentes nao podem excluir registros clinicos')
      }

      // Soft delete + audit log rodam dentro de uma RPC SECURITY DEFINER.
      // Um UPDATE direto para status = 'deleted' sempre viola RLS: a policy de
      // SELECT de patient_records exige status = 'active' e o Postgres aplica
      // essa policy na nova linha do UPDATE. A RPC checa autor/admin e grava o
      // audit log na mesma transacao.
      const { error: deleteError } = await supabase.rpc(
        'soft_delete_patient_record',
        { p_record_id: input.id },
      )

      if (deleteError) throw deleteError
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['patient-records', variables.patient_id],
      })
    },
  })
}
